import { z } from 'zod'

import { PublicSurface } from './types'

export const PUBLIC_SESSION_STORAGE_KEY = 'buffer:publish:public-analytics'
export const PUBLIC_VISITOR_STORAGE_KEY = 'buffer:publish:public-visitor'
export const PUBLIC_SESSION_TTL_MS = 30 * 60 * 1000

const SessionSchema = z.object({
  id: z.string().uuid(),
  entrySource: z.string().max(200),
  startedAt: z.number().finite(),
  lastActionAt: z.number().finite(),
  actionCount: z.number().int().nonnegative(),
  surfaces: z.array(z.nativeEnum(PublicSurface)),
  consentVersion: z.string().default(''),
})

export type PublicSession = z.infer<typeof SessionSchema>

export function createPublicSessionStore({
  storage,
  now,
  generateId,
  getConsentVersion,
  onSave,
}: {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  now: () => number
  generateId: () => string
  getConsentVersion: () => string
  onSave?: () => void
}): {
  read: () => PublicSession | undefined
  start: (entrySource: string) => PublicSession
  save: (session: PublicSession) => void
  clear: () => void
} {
  let memory: PublicSession | undefined
  const clear = (): void => {
    memory = undefined
    try {
      storage.removeItem(PUBLIC_SESSION_STORAGE_KEY)
    } catch {
      /* Storage may be disabled. */
    }
  }
  const save = (session: PublicSession): void => {
    memory = session
    try {
      onSave?.()
      storage.setItem(PUBLIC_SESSION_STORAGE_KEY, JSON.stringify(session))
    } catch {
      /* Keep this page's session in memory. */
    }
  }
  const read = (): PublicSession | undefined => {
    if (!memory) {
      try {
        const parsed = SessionSchema.safeParse(
          JSON.parse(storage.getItem(PUBLIC_SESSION_STORAGE_KEY) ?? 'null'),
        )
        if (parsed.success) memory = parsed.data
      } catch {
        return undefined
      }
    }
    // Marker expiry does not revoke a newer active session. Inactive sessions expire with the marker.
    const consentVersion = getConsentVersion()
    if (
      memory &&
      ((consentVersion !== '' && memory.consentVersion !== consentVersion) ||
        now() - memory.lastActionAt >= PUBLIC_SESSION_TTL_MS ||
        memory.startedAt > now() ||
        memory.lastActionAt > now())
    ) {
      clear()
    }
    return memory
  }
  return {
    read,
    save,
    clear,
    start(entrySource): PublicSession {
      const session = {
        id: generateId(),
        entrySource,
        startedAt: now(),
        lastActionAt: now(),
        actionCount: 0,
        surfaces: [],
        consentVersion: getConsentVersion(),
      }
      save(session)
      return session
    },
  }
}
